import { Button, Card, Col, Container, ListGroup, ListGroupItem, Row } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';

import './Trackboard.css';

import { TrackboardItem } from './TrackboardItem';

import { faChevronDown, faChevronLeft, faChevronUp, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import pLimit from "p-limit";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FhirPatientVisit, callFhirPatient } from "../../amplify/api-gateway/fetch-sync-fhir";
import { CDU_PATIENT_VISITS_WITH_METADATA, Diagnosis, FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS, FAVORITE_LISTS_FOR_USERNAME, Favorite, Note, PAST_FAVORITES_FOR_CDU_PATIENTS, RELEVANT_COMPASS_DIAGNOSES_FOR_CDU_PATIENTS, callQuery } from "../../amplify/api-gateway/query";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/api-gateway/search-patients';
import { isDemoUser } from "../../utils/demo";
import { getStorage } from "../../utils/storage";
import { useGlobalOptions } from "../common/GlobalOptionsContext";
import { FavoriteColumnSelectModal } from "./FavoriteColumnSelectModal";
import { FavoriteContextMenu } from "./FavoriteContextMenu";
import { FavoriteNewListModal } from "./FavoriteNewListModal";
import { formatCsnToFavorites, getDefaultAugmentedPatient, getDefaultColumns, getDemoMrnToName, sortPatientsByCol, sortPatientsDefault, updatePatientWithMrnToName } from "./trackboardUtils";


export const TrackboardCDU = (props: AppAuthStateProps) => {

    const { getDerivedRole, getTrackboardColumns } = useGlobalOptions();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false)
    const [sortColId, setSortColId] = useState<string>();
    const [sortColAsc, setSortColAsc] = useState<boolean>(true);
    const [allPatients, setAllPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [activePatients, setActivePatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [mrnToDiagnosisStr, setMrnToDiagnosisStr] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [csnToReviewed, setCsnToReviewed] = useState<any>({});
    const [mrnToPastFavorites, setMrnToPastFavorites] = useState<any>({});
    const [mrnToPastReviewed, setMrnToPastReviewed] = useState<any>({});
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({x: 0, y: 0, csn: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);
    const [columns, setColumns] = useState<any[]>([]);

    useEffect(() => {
        const colsFromSettings = getTrackboardColumns();
        const role = getDerivedRole(props.user);
        setColumns(getDefaultColumns(role, colsFromSettings));
    }, [getTrackboardColumns, getDerivedRole, props.user]);

    const sortPatients = (col: string) => {
        setActivePatients((activePts: any) => {
            const pts = [...activePts];
            let asc = sortColAsc;
            if (sortColId === col) {
                if (sortColAsc === false) {
                    // Reset to original state
                    col = "bed";
                    setSortColId("bed");
                    setSortColAsc(true);
                    asc = true;
                } else {
                    asc = false;
                    setSortColAsc(asc);
                }
            } else {
                setSortColId(col);
                setSortColAsc(true);
            }
    
            return sortPatientsByCol(pts, col, asc, props.tenant);
        });
    }

    const loadPatientNames = async (patients: PatientVisit[]) => {
        const limit = pLimit(2);
        const localMrnToName: any = {
            ...mrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            if (!(mrn in localMrnToName)) {
                promises.push(limit(() => {
                    return new Promise((resolve: any, reject: any) => {
                        callFhirPatient({
                            mrn: patient.mrn,
                        }).then((pt: FhirPatientVisit) => {
                            localMrnToName[patient.mrn] = pt;
                            // Update the names slowly, to avoid hogging the bandwidth
                            // which will prevent users from clicking on the patients
                            if (Object.keys(localMrnToName).length % 10 === 0) {
                                setMrnToName({
                                    ...localMrnToName
                                });
                            }
                            resolve(pt)
                        }).catch((err: any) => {
                            reject({});
                        })
                    })
                }))
            }
        }

        const result = await Promise.all(promises);
        for (let fhirPatientInfo of result) {
            localMrnToName[fhirPatientInfo.mrn] = fhirPatientInfo;
        }
        setMrnToName(localMrnToName);
    }
    
    const refreshBaseData = () => {
        setIsLoading(true);
        callQuery({
            operation: CDU_PATIENT_VISITS_WITH_METADATA,
        })
        .then((patients: PatientVisit[]) => {
            sortPatientsDefault(patients);

            if (!isDemoUser(props.user, props.tenant)) {
                loadPatientNames(patients).then(() => {});
            } else {
                setMrnToName(getDemoMrnToName(patients));
            }
            setIsLoading(false);

            const augmentedPatients = patients.map((p: PatientVisit) => {
                return getDefaultAugmentedPatient(p);
            });
            setAllPatients(augmentedPatients);
            setActivePatients(augmentedPatients);
        });

        callQuery({
            operation: RELEVANT_COMPASS_DIAGNOSES_FOR_CDU_PATIENTS,
        })
        .then((relevantDiagnoses: Diagnosis[]) => {
            const mrnToDiagnoses: any = {};
            if (relevantDiagnoses) {
                for (let diagnosis of relevantDiagnoses) {
                    if (!(diagnosis.mrn in mrnToDiagnoses)) {
                        mrnToDiagnoses[diagnosis.mrn] = new Set();
                    }
                    mrnToDiagnoses[diagnosis.mrn].add(diagnosis.desc10);
                }
                const mrnToDiagnosisStr: any = {};
                for (let mrn of Object.keys(mrnToDiagnoses)) {
                    mrnToDiagnosisStr[mrn] = Array.from(mrnToDiagnoses[mrn]).join(",");
                }
                setMrnToDiagnosisStr(mrnToDiagnosisStr);
            }
        });

        callQuery({
            operation: FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS,
        })
        .then((scores: any) => {
            const mapFavorites: any = {};
            const mapReviewed: any = {};
            for (let score of scores) {
                const csn = score["csn"];
                if (score["favorite_type"] === "reviewed") {
                    mapReviewed[csn] = score;
                } else if (score["favorite_type"] !== "recent") {
                    if (!(csn in mapFavorites)) {
                        mapFavorites[csn] = [];
                    }
                    mapFavorites[csn].push(score);
                } 
            }
            setCsnToFavorites(mapFavorites);
            setCsnToReviewed(mapReviewed);
        });

        callQuery({
            operation: PAST_FAVORITES_FOR_CDU_PATIENTS,
        })
        .then((favorites: any) => {
            const mapFavorites: any = {};
            const mapReviewed: any = {};
            for (let favorite of favorites) {
                console.log("updating favorite", favorite)
                const mrn = favorite["mrn"];
                if (favorite["favorite_type"] === "reviewed") {
                    mapReviewed[mrn] = favorite;
                } else if (favorite["favorite_type"] !== "recent") {
                    if (!(mrn in mapFavorites)) {
                        mapFavorites[mrn] = [];
                    }
                    mapFavorites[mrn].push(favorite);
                }
            }
            setMrnToPastFavorites(mapFavorites);
            setMrnToPastReviewed(mapReviewed);
        });

        callQuery({
            operation: FAVORITE_LISTS_FOR_USERNAME,
        })
        .then((lists: any) => {
            lists = [
                "favorite",
                ...lists.map((list: any) => list.list_name)
            ];
            setFavoriteLists(lists);
        });
    }

    useEffect(() => {
        refreshBaseData();
    }, []);
    
    useEffect(() => {
        setAllPatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, mrnToName);
                })),
            ];
        });
        setActivePatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, mrnToName);
                })),
            ];
        })
    }, [mrnToName]);


    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        console.log("updateCsnToFavorites", favoriteType)
        const map: any = {...csnToFavorites};
        formatCsnToFavorites(map, csn, favoriteType, favorite);
        setCsnToFavorites(map);
    };

    const updatePatientWithNote = (note: Note) => {
        setActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} onClick={handleClear} fluid>

                <Row style={{marginBottom: '12px'}}>
                    <Col md={12}>
                        <div style={{width: '100%', verticalAlign: 'middle'}}>
                            <Button style={{float: 'left'}} size="sm" variant={"outline-secondary"} className="trackboard-quick-actions-btn" onClick={() => { navigate("/trackboard-v2"); }}><FontAwesomeIcon icon={faChevronLeft} /> Trackboard</Button>{' '}
                            <h5 style={{float: 'left', marginTop: '4px', marginLeft: '16px'}}>CDU Patients</h5>
                        </div>
                    </Col>
                </Row>

                <Row style={{marginBottom: '24px'}}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div style={{display: 'block', width: '100%'}} >
                                        {
                                            columns.map((col) => {
                                                const totalRelativeCols = columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);
                                                if (col.id === "action") {
                                                    return (
                                                        <div key={col.id} className="text-right" style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>
                                                            <Button
                                                                variant="primary"
                                                                className={"trackboard-show-btn"}
                                                                onClick={() => setShowColumnSelect(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </Button>
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div key={col.id} style={{display: 'inline-block', cursor: 'pointer', width: `${100 * col.size/totalRelativeCols}%`}} onClick={() => sortPatients(col.id)}>{col.name}{sortColId === col.id ? sortColAsc ? <>{' '}<FontAwesomeIcon icon={faChevronDown} /></> : <>{' '}<FontAwesomeIcon icon={faChevronUp} /></> : <></>}</div>
                                                    )
                                                }
                                            })
                                        }
                                    </div>
                                </ListGroupItem>
                                {
                                    isLoading ? (
                                        <>
                                            {
                                                [1, 2, 3].map((v) => (
                                                    <TrackboardItem 
                                                        key={v}
                                                        /** @ts-ignore */
                                                        patient={null}
                                                        columns={columns} 
                                                        csnToFavorites={csnToFavorites}
                                                        csnToReviewed={csnToReviewed}
                                                        pastFavorites={[]}
                                                        pastReviewed={[]}
                                                        setCsnToReviewed={setCsnToReviewed}
                                                        setShowContextMenu={setShowContextMenu}
                                                        setContextMenu={setContextMenu}
                                                        padNotes={true}
                                                        isPlaceholder={true}
                                                        updatePatientWithNote={updatePatientWithNote}
                                                        tenant={props.tenant}
                                                    />
                                                ))
                                            }
                                        </>
                                    ) : (
                                        <>{
                                            activePatients.map((pt) => (
                                                <TrackboardItem 
                                                    key={pt.csn}
                                                    patient={pt}
                                                    columns={columns}
                                                    csnToFavorites={csnToFavorites}
                                                    csnToReviewed={csnToReviewed}
                                                    pastFavorites={mrnToPastFavorites[pt.mrn]}
                                                    pastReviewed={mrnToPastReviewed[pt.mrn]}
                                                    setCsnToReviewed={setCsnToReviewed}
                                                    padNotes={true}
                                                    setShowContextMenu={setShowContextMenu}
                                                    setContextMenu={setContextMenu}
                                                    updatePatientWithNote={updatePatientWithNote}
                                                    mrnToRelevantPastDiagnosesForCompass={mrnToDiagnosisStr}
                                                    tenant={props.tenant}
                                                    user={props.user}
                                                />
                                            ))
                                        }</>
                                    )
                                }
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                <FavoriteContextMenu show={showContextMenu} contextMenu={contextMenu} lists={favoriteLists} setShow={setShowContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={contextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteColumnSelectModal show={showColumnSelect} setShow={setShowColumnSelect} columns={columns} setColumns={setColumns} allColumns={getDefaultColumns(getDerivedRole(props.user))} saveKey="trackboard_columns" />
            </Container>
        </>
    );
};
